import ICashShopEntry, {
    CashShopBadge,
    CashCommodityType,
    ICashShopSubTab,
    ICashShopTab,
} from '@/models/items/ICashShop';

//  Project Duck tab remap (DnCashShopTask.cpp:6878-6921): commodity categories
//  >= 2 are bumped +1 before matching a cashtabtable _MTNum; category 1 tries
//  tab 1 and falls back to tab 2. These two functions are the inverse (for
//  per-tab queries) and forward (for placing a known entry) forms.
export function tabNumToCategory(tabNum: number): number {
    if (tabNum >= 3) {
        return tabNum - 1;
    }
    if (tabNum === 2) {
        return 1;
    }
    return tabNum;
}

export function categoryToTabNum(category: number, subCategory: number, tabs: ICashShopTab[]): number | null {
    const candidates = category === 1 ? [1, 2] : category >= 2 ? [category + 1] : [category];
    for (const c of candidates) {
        const tab = tabs.find((t) => t.mtNum === c);
        if (tab && tab.subTabs.some((s) => s.stNum === subCategory)) {
            return c;
        }
    }
    const existing = candidates.find((c) => tabs.some((t) => t.mtNum === c));
    return existing !== undefined ? existing : null;
}

//  itemtable._NeedJobClass: "0"/"" means usable by every class; otherwise a
//  ;-separated list of job ids (any depth in the job tree, e.g. "1;2;3;4").
export function parseNeedJobClass(text: string): number[] {
    if (!text) {
        return [];
    }

    return text
        .split(';')
        .map((t) => Number(t))
        .filter((n) => n > 0);
}

//  Walk parentJobId links to the root (base class). Bounded in case of data
//  cycles; unknown jobs resolve to themselves.
export function resolveBaseJob(jobId: number, parentByJob: { [id: number]: number }): number {
    let cur = jobId;
    for (let i = 0; i < 8; i++) {
        const parent = parentByJob[cur];
        if (!parent) {
            return cur;
        }
        cur = parent;
    }

    return cur;
}

export function entryMatchesBaseJob(needJobIds: number[], baseJobId: number, parentByJob: { [id: number]: number }): boolean {
    if (!needJobIds.length) {
        return true;
    }

    return needJobIds.some((id) => resolveBaseJob(id, parentByJob) === baseJobId);
}

const BADGE_TOKENS = new Set<string>(Object.values(CashShopBadge));

export function parseBadges(stateDuplicate: string): CashShopBadge[] {
    if (!stateDuplicate) {
        return [];
    }

    return stateDuplicate
        .split(';')
        .filter((t) => BADGE_TOKENS.has(t)) as CashShopBadge[];
}

//  _PeriodType (DNCommonDef.h PeriodTimeType): 0 Unlimited, 1 Year, 2 Month,
//  3 Day, 4 Hour, 5 Minute. _Period <= 0 means permanent regardless.
const PERIOD_UNITS: { [k: number]: string } = {
    1: 'year',
    2: 'month',
    3: 'day',
    4: 'hour',
    5: 'minute',
};

export function formatPeriod(period: number, periodType: number): string {
    if (periodType === 0 || period <= 0) {
        return 'Permanent';
    }

    const unit = PERIOD_UNITS[periodType] || '?';
    return `${period} ${unit}${period !== 1 && unit !== '?' ? 's' : ''}`;
}

export function vipPrice(price: number, paySale: number): number {
    return Math.floor((price * (100 - paySale)) / 100);
}

export function mapCommodityRow(row: Record<string, any>): ICashShopEntry {
    const itemIds: number[] = [];
    const linkSns: number[] = [];
    for (let i = 1; i <= 5; i++) {
        const itemId = Number(row['_ItemID0' + i]);
        if (itemId) {
            itemIds.push(itemId);
        }
        const linkSn = Number(row['_LinkSN0' + i]);
        if (linkSn) {
            linkSns.push(linkSn);
        }
    }

    const price = Number(row._Price) || 0;
    const paySale = Number(row._PaySale) || 0;

    return {
        sn: Number(row._SN),
        category: Number(row._Category),
        subCategory: Number(row._SubCategory),
        nameId: Number(row._NameID),
        name: String(row._NameID_txt || ''),
        descriptionId: Number(row._DescriptionID),
        description: String(row._DescriptionID_txt || ''),
        itemIds,
        linkSns,
        count: Number(row._Count) || 1,
        price,
        priceFix: Number(row._PriceFix) || 0,
        paySale,
        vipPrice: vipPrice(price, paySale),
        periodText: formatPeriod(Number(row._Period), Number(row._PeriodType)),
        badges: parseBadges(String(row._StateDuplicate || '')),
        type: Number(row._ItemSort) as CashCommodityType,
        onSale: !!row._OnSale,
        onSaleReal: !!row._OnSaleREAL,
        giftable: !!row._PresentAble,
        //  Duck maps DNP purchasability onto _ReserveAble (stargazer-rs
        //  acquire/cash.rs canUseDnp).
        dnpAble: !!row._ReserveAble,
        limited: !!row._Limit,
        overlapBuy: Number(row._OverlapBuy) || 0,
        buyLevel: Number(row._buylevel) || 0,
        priority: Number(row._Priority) || 0,
        currencies: {
            cash: !!row._CashAble,
            snda: !!row._SNDACash,
            third: !!row._3rdCash,
            seed: !!row._SeedAble,
        },
    };
}

//  cashtabtable: one row per main tab; sub-tab slots 0..8 as _STNum{i} /
//  _STShow{i} / _ST{i}ID. A slot with no name id is unused. Show value 1 is
//  visible; 0 (hide) and 2 (disable) are not.
export function readTabs(rows: Record<string, any>[]): ICashShopTab[] {
    const tabs: ICashShopTab[] = rows.map((row) => {
        const subTabs: ICashShopSubTab[] = [];
        for (let i = 0; i <= 8; i++) {
            const nameId = Number(row['_ST' + i + 'ID']);
            if (!nameId) {
                continue;
            }

            subTabs.push({
                stNum: Number(row['_STNum' + i]),
                nameId,
                name: '',
                visible: Number(row['_STShow' + i]) === 1,
            });
        }

        return {
            mtNum: Number(row._MTNum),
            nameId: Number(row._MTID),
            name: '',
            visible: Number(row._MTShow) === 1,
            subTabs,
        };
    });

    return tabs.sort((a, b) => a.mtNum - b.mtNum);
}

export function sortEntries(entries: ICashShopEntry[]): ICashShopEntry[] {
    return [...entries].sort((a, b) => b.priority - a.priority || a.name.localeCompare(b.name));
}
